'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_ACTIVE_CONTEXT,
  setActiveContext,
  subscribeActiveContext,
} from '@kiao-voice/api-client'
import { useOrganizationIdentity } from '@kiao-voice/hooks'
import { IconButton, SideNavItem, toast } from '@kiao-voice/ui-kit'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { APP_ROUTES } from '@/lib/app-routes'
import { listContexts, type SwitchableContext } from '../features/team/api-client'
import { fetchMe, onAuthTokenChange, readStoredToken } from '../features/auth/auth-storage'
import { LogoutFlash } from '../features/auth/logout-flash'
import { signOut as defaultSignOut } from '../features/batch-transcription/auth-storage'
import { useUploadInProgress } from '../features/batch-transcription/upload-activity'
import { useUploadUnloadGuard } from '../features/batch-transcription/use-upload-unload-guard'
import { pathnameToAppShellActive, type AppShellActive } from '../lib/app-shell-active'
import { useSidebarNav } from '../hooks/use-sidebar-nav'
import { subscribeWorkspaceRecovery } from '../lib/workspace-recovery'
import { subscribeMemberAccessRefresh } from '../lib/member-access-refresh'
import {
  isAccessDenialCurrent,
  resolveWorkspaceGate,
  type AccessDenial,
} from '../features/team/team-billing-state'
import { AccountMenu, type AccountContextView } from './account-menu'
import { BrandLogo } from './brand-logo'
import { DisabledWorkspaceNotice } from './disabled-workspace-notice'
import { WorkspaceIndicatorProvider } from './workspace-indicator'

export type { AppShellActive }

interface AppShellProps {
  /** Which sidebar nav item to mark with `aria-current="page"`. */
  active: AppShellActive
  /** Signed-in user's email, shown in the account row. */
  email: string | null
  /** Fired when the user clicks 登出 (only rendered inside the account menu). */
  onSignOut: () => void
  /**
   * The caller's switchable account contexts (Claude-style workspace switcher).
   * Drives everything role-dependent: `length > 1` renders the "切換工作區"
   * switcher; the SELECTED context gates the Team nav (shown only for a team
   * the user owns); `length === 1` (personal only) is a SOLO user — the Team nav
   * is hidden and the account menu shows a 「了解團隊方案 ↗」 upsell instead.
   */
  contexts?: SwitchableContext[]
  /** The currently selected context key (`'personal'` or `org:<id>`). */
  activeContext?: string
  /** Fired when the user picks a different workspace. Defaults to the store. */
  onSelectContext?: (key: string) => void
  /** Re-reads the contexts (and so the caller's access to them). */
  onRecheckAccess?: () => void
  /** Test seam; production reads the process-wide upload activity store. */
  uploadInProgress?: boolean
  /** Test seam; production replaces the current route with the workspace home. */
  navigateToWorkspaceHome?: (path: string) => void
  /** Test seam; production renders a warning toast. */
  notifyWorkspaceSwitchBlocked?: (message: string) => void
  /** Test seam; production renders the workspace recovery toast. */
  notifyWorkspaceRecovery?: (message: string) => void
  children: React.ReactNode
}

/**
 * The logged-in app shell — the sidebar layout shared by the batch
 * transcription, account, team, and transcripts pages.
 *
 * Structure and CSS classes follow the Open Design app-account / app-team /
 * app-transcribe prototypes (the `.shell` grid with a sticky `.sidebar` and a
 * `.main` content column). The bottom-left account identity zone is the shared
 * {@link AccountMenu}. All chrome styles come from the global ui-kit
 * `kiao-app.css`; this component writes no CSS.
 */
export function AppShell({
  active,
  email,
  onSignOut,
  contexts,
  activeContext,
  onSelectContext = setActiveContext,
  onRecheckAccess,
  uploadInProgress,
  navigateToWorkspaceHome,
  notifyWorkspaceSwitchBlocked = (message) => toast.info(message),
  notifyWorkspaceRecovery = (message) => toast.info(message),
  children,
}: AppShellProps) {
  const { t } = useTranslation(['common', 'web'])
  const router = useRouter()
  const observedUploadInProgress = useUploadInProgress()
  const isUploadInProgress = uploadInProgress ?? observedUploadInProgress
  // Closing the tab mid-upload discards bytes that exist nowhere else and leaves
  // nothing in the list to explain it. Mounted here rather than in the dialog so
  // it still holds while the user browses to another page of the app.
  useUploadUnloadGuard()

  useEffect(
    () =>
      subscribeWorkspaceRecovery(({ workspaceKey }) => {
        const teamName =
          contexts?.find((context) => context.key === workspaceKey)?.name ??
          t('web:appShell.unknownTeam')
        notifyWorkspaceRecovery(t('web:appShell.workspaceRecovered', { team: teamName }))
        const navigateHome = navigateToWorkspaceHome ?? router.replace
        navigateHome(APP_ROUTES.transcriptions)
      }),
    [contexts, navigateToWorkspaceHome, notifyWorkspaceRecovery, router, t]
  )

  useEffect(
    () =>
      subscribeActiveContext((_workspaceKey, reason) => {
        // A storage clear can come from another tab signing out. In that case
        // the auth-token listener owns navigation; only an authenticated
        // workspace switch/recovery is this shell's hard boundary.
        if (reason !== 'storage' || !readStoredToken()) return
        const navigateHome = navigateToWorkspaceHome ?? router.replace
        navigateHome(APP_ROUTES.transcriptions)
      }),
    [navigateToWorkspaceHome, router]
  )

  // Mobile nav drawer: on ≤720px the sidebar links collapse behind a ☰ (the
  // account menu stays as its own avatar trigger). Mirrors PublicNavbar's
  // hamburger so the two navbars behave identically on phones. Shared with
  // AdminShell via the hook.
  const { navOpen, toggleNav, closeNav, sidebarRef, navToggleRef } = useSidebarNav()

  const selected = useMemo(
    () => contexts?.find((c) => c.key === activeContext) ?? null,
    [contexts, activeContext]
  )

  // A page's content request was refused with `team_member_disabled`: close the
  // page at once and re-read the contexts, which then carry the disabled state.
  // Held until contexts read after it show the seat usable, so a failed re-read
  // keeps the page closed; switching to another workspace drops it.
  const [accessDenial, setAccessDenial] = useState<AccessDenial | null>(null)
  useEffect(() => {
    setAccessDenial((denial) => (denial && denial.key !== activeContext ? null : denial))
  }, [activeContext])
  useEffect(
    () =>
      subscribeMemberAccessRefresh(({ workspaceKey }) => {
        const key = workspaceKey ?? activeContext
        if (!key || key !== activeContext) return
        setAccessDenial({ key, contexts })
        onRecheckAccess?.()
      }),
    [activeContext, contexts, onRecheckAccess]
  )
  // Picking a team whose seat is disabled: the open page is swapped for the
  // notice first and the store is written after that commit. Writing it at once
  // would let the page's own workspace subscription query the refused team
  // before this shell re-renders.
  const [pendingDisabledKey, setPendingDisabledKey] = useState<string | null>(null)
  useEffect(() => {
    if (!pendingDisabledKey) return
    setPendingDisabledKey(null)
    onSelectContext(pendingDisabledKey)
  }, [pendingDisabledKey, onSelectContext])
  const gateSelected = pendingDisabledKey
    ? (contexts?.find((c) => c.key === pendingDisabledKey) ?? null)
    : selected
  const workspaceGate = resolveWorkspaceGate({
    selected: gateSelected,
    active,
    denied: isAccessDenialCurrent(accessDenial, activeContext, contexts),
  })

  // The server always appends a `personal` context, so 2+ contexts means at
  // least one team to switch to → render the switcher. Exactly one context
  // (personal only) is a SOLO user. `undefined` = contexts not resolved yet.
  const hasSwitcher = Boolean(contexts && contexts.length > 1)
  // Solo = the one resolved context is the personal one (the server always
  // appends a personal context, so a lone non-personal context can't normally
  // happen — but key off `mode` so a stray team-only list isn't read as solo).
  const isSolo = Boolean(contexts?.length === 1 && contexts[0]?.mode === 'personal')

  /** One-line summary of a context for the account row / switcher rows. */
  const contextLine = useCallback(
    (ctx: SwitchableContext): string =>
      ctx.mode === 'personal'
        ? t('web:account.personalContext', { plan: ctx.planLabel })
        : ctx.accessState === 'disabled'
          ? t('web:team.switcherDisabledLabel', { org: ctx.name })
          : t('web:account.teamContext', {
              org: ctx.name,
              role:
                ctx.role === 'owner'
                  ? t('web:account.roleOwnerSuffix')
                  : t('web:account.roleSeatSuffix'),
            }),
    [t]
  )

  // The Team nav (internal /account/team management) shows ONLY while acting in
  // a team the user OWNS. Hidden in every other state — personal context (even
  // for an owner: switch INTO the team to manage it), a team-MEMBER seat (the
  // page is owner-only), solo, and the unresolved loading window. Solo's team
  // entry instead lives in the account menu (「了解團隊方案 ↗」 → /pricing#team).
  const showTeamNav = selected?.mode === 'team' && selected.role === 'owner'
  // API keys are open to every org OWNER — including a solo user in their own
  // personal context, who owns that org by construction. Mirrors the backend
  // (`resolveManageOrgId` 403s `not_org_owner` and nothing else), so the tab is
  // hidden only for a seated team MEMBER and the unresolved loading window.
  // Deliberately NOT gated on `apiAccessEnabled` (`voice_input_api_enabled`):
  // that flag gates the voice-input capability, not key management, so a
  // not-yet-opened org still self-serves a batch-transcriptions key.
  const showApiKeysNav = selected?.role === 'owner'

  // Identity sub-line under the email: 個人 · {plan} for personal/solo,
  // {org} · 團隊席次 for a seat. Null until a context resolves.
  const identityLine = selected ? contextLine(selected) : null
  const workspaceIndicator = useMemo(
    () =>
      selected
        ? {
            key: selected.key,
            name: selected.mode === 'personal' ? t('web:account.personalLabel') : selected.name,
            mode: selected.mode,
          }
        : null,
    [selected, t]
  )

  // Switcher rows — empty unless there's a real choice (→ AccountMenu renders
  // no switcher and, for solo, the team upsell instead).
  const switcherRows = useMemo<AccountContextView[]>(
    () =>
      hasSwitcher && contexts
        ? contexts.map((ctx) => ({
            key: ctx.key,
            name: ctx.mode === 'personal' ? t('web:account.personalLabel') : ctx.name,
            sub: contextLine(ctx),
            isPersonal: ctx.mode === 'personal',
            isActive: ctx.key === activeContext,
          }))
        : [],
    [hasSwitcher, contexts, activeContext, contextLine, t]
  )

  const handleSelectContext = useCallback(
    (key: string) => {
      // Selecting the active context is a no-op — avoid a needless store write
      // (and the refetch it triggers).
      if (key === activeContext) return
      if (isUploadInProgress) {
        notifyWorkspaceSwitchBlocked(t('web:appShell.uploadWorkspaceSwitchBlocked'))
        return
      }
      const target = contexts?.find((c) => c.key === key)
      if (target?.accessState === 'disabled') setPendingDisabledKey(key)
      else onSelectContext(key)
      const navigateHome = navigateToWorkspaceHome ?? router.replace
      navigateHome(APP_ROUTES.transcriptions)
    },
    [
      activeContext,
      contexts,
      isUploadInProgress,
      navigateToWorkspaceHome,
      notifyWorkspaceSwitchBlocked,
      onSelectContext,
      router,
      t,
    ]
  )

  return (
    <div className="shell">
      <aside className="sidebar" ref={sidebarRef}>
        {/* Mobile-only ☰ (sits before the wordmark, per the chosen layout):
            collapses the nav links into a dropdown. Hidden on desktop. */}
        <IconButton
          ref={navToggleRef}
          type="button"
          className="app-nav-toggle"
          aria-label={t('web:navbar.menu')}
          aria-expanded={navOpen}
          aria-controls="app-shell-nav"
          onClick={toggleNav}
        >
          {navOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </IconButton>
        <div className="side-brand">
          <Link href={APP_ROUTES.transcriptions} aria-label={t('common:productName')}>
            <BrandLogo />
          </Link>
        </div>
        <nav
          id="app-shell-nav"
          className={navOpen ? 'side-nav is-open' : 'side-nav'}
          aria-label={t('web:navbar.primaryNavAriaLabel')}
          onClick={closeNav}
        >
          <p className="side-label">{t('web:appShell.groupUse')}</p>
          {/* Uploading is now a submit-only modal ON /transcriptions — there is no
              separate batch-transcription workspace in the member nav anymore. */}
          <SideNavItem
            variant="app"
            as={Link}
            href={APP_ROUTES.transcriptions}
            active={active === 'transcriptions'}
            label={t('web:appShell.navMyTranscripts')}
          />

          <p className="side-label">{t('web:appShell.groupManage')}</p>
          <SideNavItem
            variant="app"
            as={Link}
            href={APP_ROUTES.account}
            active={active === 'account'}
            label={t('web:appShell.navAccount')}
          />
          {/* 整理範本 belongs here because its scope is the whole ACCOUNT — the
              same templates apply to every transcript and to uploads.
              會議記錄 itself is deliberately NOT in this nav: it is the product
              of one particular transcript, and listing it here would claim it
              sits at the same level as 我的逐字稿. */}
          <SideNavItem
            variant="app"
            as={Link}
            href={APP_ROUTES.templates}
            active={active === 'templates'}
            label={t('web:appShell.navTemplates')}
          />
          {/* Same account-wide scope as 整理範本 above: one word list shared by
              every upload and every dictation, not tied to one transcript. */}
          <SideNavItem
            variant="app"
            as={Link}
            href={APP_ROUTES.dictionaries}
            active={active === 'dictionaries'}
            label={t('web:appShell.navDictionaries')}
          />
          {showApiKeysNav && (
            <SideNavItem
              variant="app"
              as={Link}
              href={APP_ROUTES.apiKeys}
              active={active === 'api-keys'}
              label={t('web:appShell.navApiKeys')}
            />
          )}
          <SideNavItem
            variant="app"
            as={Link}
            href={APP_ROUTES.referral}
            active={active === 'referral'}
            label={t('web:appShell.navReferral')}
          />
          {showTeamNav && (
            <SideNavItem
              variant="app"
              as={Link}
              href={APP_ROUTES.team}
              className="nav-team"
              active={active === 'team'}
              label={t('web:appShell.navTeam')}
            />
          )}
          <SideNavItem
            variant="app"
            href="/pricing"
            target="_blank"
            rel="noreferrer"
            label={
              <>
                {t('web:appShell.navPricing')}{' '}
                <span className="meta" aria-hidden="true">
                  ↗
                </span>
              </>
            }
          />
        </nav>

        <AccountMenu
          email={email}
          identityLine={identityLine}
          contexts={switcherRows}
          onSelectContext={handleSelectContext}
          showTeamUpsell={isSolo}
          onSignOut={onSignOut}
        />
      </aside>

      <main className="main">
        <WorkspaceIndicatorProvider value={workspaceIndicator}>
          {/* A disabled team's pages are never mounted, so none of their queries
              run and no cached team content shows; /app/account stays reachable. */}
          {workspaceGate === 'disabled_notice' ? (
            <DisabledWorkspaceNotice
              onSwitchWorkspace={() => handleSelectContext(DEFAULT_ACTIVE_CONTEXT)}
              onRecheck={() => onRecheckAccess?.()}
            />
          ) : (
            children
          )}
        </WorkspaceIndicatorProvider>
      </main>
    </div>
  )
}

interface ConnectedAppShellProps {
  /**
   * Which sidebar nav item to mark current. Optional — when omitted the shell
   * derives it from the App Router pathname (so a route-group layout can mount
   * the shell once without each page threading the prop). An explicit value
   * wins, preserving the per-page test seams.
   */
  active?: AppShellActive
  /**
   * Signed-in email when the page already knows it (e.g. the batch page's
   * bootstrap probe). When omitted, the shell resolves it itself via
   * `fetchEmailFn` so AuthGuard-wrapped pages can mount the shell without
   * plumbing session state.
   */
  email?: string | null
  children: React.ReactNode
  /** Test seam — defaults to `readStoredToken()` + `/auth/me`. */
  fetchEmailFn?: () => Promise<string | null>
  /**
   * Test seam — defaults to `readStoredToken()` + `GET /organizations/contexts`.
   * Drives the workspace switcher + the Team-nav gating. `null`, or a rejection
   * before any list has loaded, degrades to an email-only row (no switcher) so
   * the menu never breaks; a later rejection keeps the last list.
   */
  fetchContextsFn?: () => Promise<SwitchableContext[] | null>
  /** Test seam — defaults to the real sign-out (server revoke + token clear). */
  signOutFn?: () => Promise<void>
  /** Test seam — defaults to a real Next.js router push. */
  navigate?: (path: string) => void
}

async function defaultFetchEmail(): Promise<string | null> {
  const token = readStoredToken()
  if (!token) return null
  const res = await fetchMe(token)
  return res.user.email
}

async function defaultFetchContexts(): Promise<SwitchableContext[] | null> {
  const token = readStoredToken()
  if (!token) return null
  return listContexts()
}

/**
 * AppShell wired to the auth session: resolves the account row's email + the
 * switchable contexts in parallel, then handles 登出 (instant LogoutFlash).
 * Each branch degrades gracefully — a missing token or a failed call falls back
 * so the shell never blanks.
 */
export function ConnectedAppShell({
  active,
  email,
  children,
  fetchEmailFn = defaultFetchEmail,
  fetchContextsFn = defaultFetchContexts,
  signOutFn = defaultSignOut,
  navigate,
}: ConnectedAppShellProps) {
  const pathname = usePathname()
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(email ?? null)
  const [signingOut, setSigningOut] = useState(false)
  // Bumped on a token change so the shared org-identity hook re-fetches the
  // switchable contexts when an in-place sign-in lands (no remount). It is part
  // of `loadContexts`' identity, which the hook keys its fetch off.
  const [authNonce, setAuthNonce] = useState(0)
  // The stored access token, as the contexts' identity: a list read with one
  // token never answers for another (a failed read keeps the list only while the
  // token is unchanged). Held in memory only; never logged or rendered.
  const [sessionToken, setSessionToken] = useState<string | null>(readStoredToken)

  const resolvedActive = active ?? pathnameToAppShellActive(pathname)

  // Switchable contexts + active-key resolution + smart-default adoption all come
  // from the shared hook. `enabled` stays true: the (app) layout mounts the shell
  // once and the loader returns an empty list when there's no token (email-only
  // row). `adoptDefaultToStore` PERSISTS the server's smart default to the shared
  // store so child pages that read it directly (e.g. billing-page resolves it for
  // per-context data, and request wrappers OMIT the header when unset) scope to
  // the same `isDefault` context — otherwise /account would show the team credit
  // balance with personal top-up controls (Codex P1 on PR #156).
  const loadContexts = useCallback(
    () => fetchContextsFn().then((contexts) => ({ contexts: contexts ?? [] })),
    [fetchContextsFn, authNonce]
  )
  const org = useOrganizationIdentity({
    enabled: true,
    loadContexts,
    identityKey: sessionToken,
    adoptDefaultToStore: true,
  })

  // Email resolution — independent of org identity (the hook owns the contexts).
  // The shell can mount BEFORE sign-in (the (app) layout sits OUTSIDE the
  // page-level AuthGuard, and #164's sign-in is an in-place modal with no
  // reload), so re-resolve on a token change — and bump `authNonce` so the
  // contexts hook re-fetches too — flipping the sidebar from signed-out to the
  // real session without a full remount.
  useEffect(() => {
    let cancelled = false

    const resolveEmail = () => {
      const emailPromise: Promise<string | null> =
        email !== undefined ? Promise.resolve(email) : fetchEmailFn()
      void emailPromise
        .then((value) => {
          if (!cancelled) setResolvedEmail(value)
        })
        .catch((err) => {
          reportHandledException(err, { source: 'components/app-shell.tsx' })
          // Report + visible recovery: omit the email line. Auth is still
          // enforced by AuthGuard / the page bootstrap.
          console.warn('[app-shell] could not resolve the account email:', err)
          if (!cancelled) setResolvedEmail(null)
        })
    }

    resolveEmail()
    setSessionToken(readStoredToken())
    const stop = onAuthTokenChange(() => {
      resolveEmail()
      setSessionToken(readStoredToken())
      setAuthNonce((n) => n + 1)
    })
    return () => {
      cancelled = true
      stop()
    }
  }, [email, fetchEmailFn])

  // Instant, non-destructive sign-out: flip to the shared <LogoutFlash>,
  // which fires the revoke and sends the user home when its dwell elapses.
  const handleSignOut = useCallback(() => setSigningOut(true), [])

  return (
    <>
      <AppShell
        active={resolvedActive}
        email={resolvedEmail}
        contexts={org.contexts}
        activeContext={org.activeKey}
        onSelectContext={org.selectContext}
        onRecheckAccess={org.refetch}
        onSignOut={handleSignOut}
      >
        {children}
      </AppShell>
      {signingOut ? <LogoutFlash signOutFn={signOutFn} navigate={navigate} /> : null}
    </>
  )
}
